import GameSignals from '@/signals/GameSignals'
import { GameStatesEnum } from '@/states/GameStatesEnum'
import { RoundRestoreUtil } from '@/utils/RoundRestoreUtil'
import { BaseState } from '@stateMachine/BaseState'
import { TranslationUtils } from '@modules/src/utils/TranslationUtils'
import { WinningUtil } from '@/managers/winning/WinningUtil'
import { SymbolData } from '@/modules'
import { SpecialSymbolType } from '@/containers/symbols/SpecialSymbolType'
import { Utilities } from '@base/utils/Utilities'
import Facade from '@/Facade'
import { isHoldAndWinInsideFreeSpins } from '@/states/freespins/FreeSpinsFinalWin'

export class RestoreState extends BaseState {
    private _nextState: GameStatesEnum
    private _autoContinueTimeoutId: number = null

    public begin(): void {
        this._nextState = GameStatesEnum.IDLE_STATE
        ;(Facade as any).holdAndWinCollectInProgress = false
        ;(Facade as any).holdAndWinPendingBaseTransition = false

        const spinData = this._model.spinData
        if (!spinData) {
            this.end()
            return
        }

        const { base_win_currency, freebet } = spinData
        const nextRoundType = String(spinData?.next_round_type || '').toLowerCase()
        const respinsLeft = Number(spinData?.respins_left ?? 0)
        const hasRespinsRestore =
            (nextRoundType === 'respins' || nextRoundType === 'respin') && Number.isFinite(respinsLeft) && respinsLeft > 0

        if (hasRespinsRestore) {
            this._restoreRespinsSession()
            return
        }

        const promoType = String((spinData as any)?.promo_details?.type || '').toLowerCase()
        const isNextRoundFreeSpins = nextRoundType === 'freespins' || nextRoundType === 'freespin'
        const isPendingPromoAward =
            !this._model.isPromoSpinsRound() &&
            (promoType === 'promospins' || promoType === 'promospin') &&
            !isNextRoundFreeSpins

        if (!!freebet && !!freebet.amount_left && !this._model.isPromoSpinsRound() && !isPendingPromoAward) {
            this._restoreFreeSpinsSession()
            return
        }

        if (
            !this._model.spinData.freeround &&
            Utilities.isLocalStorageAvailable() &&
            JSON.parse(localStorage.getItem(`${Facade.urlParams.playerUid}_freeround`))
        ) {
            this._model.spinData.freeround = JSON.parse(localStorage.getItem(`${Facade.urlParams.playerUid}_freeround`))
        }

        if (false) {
            this._model.bigWinLevel = WinningUtil.getBigWinLevel(base_win_currency, this._model.currentBaseBet)

            if (!!freebet) {
                // skipping summary dialog, if round active flag is not set
                if (RoundRestoreUtil.isRoundActive() && !!freebet.amount_left) {
                    this._restoreFreeSpinsSession()
                    return
                }
            }

            if (!!this._model.spinData.freeround) {
                if (RoundRestoreUtil.isRoundActive() || !!this._model.spinData.freeround.amount_left) {
                    this._restoreFreeRoundsSession()
                    return
                }
            }

            this._restoreSession()
            return
        }

        this.end()
    }

    private _restoreFreeRoundsSession(): void {
        GameSignals.freeRoundsActive.emit()
        this.end()
    }

    private _restoreFreeSpinsSession(): void {
        const { bonus, freebet, win_currency } = this._model.spinData

        this._nextState = GameStatesEnum.START_ROUND_STATE
        this._model.roundState.isRestoredRound = true
        this._model.roundState.isFreeSpins = true
        this._model.isBonusGameNow = true
        this._model.isFreeSpinsRestore = true
        this._model.winCounter.startRound()

        this._waitRestoreConfirmationOrContinue()
        GameSignals.enableGamePopups.emit()
        GameSignals.showFreeSpinsBackground.emit()
        GameSignals.restoreFreeSpins.emit(this._model.spinData)
        GameSignals.previousRoundActive.emit()
        GameSignals.roundStart.emit(true)

        if (freebet.amount_left === freebet.total_amount_given) {
            bonus.freebet_given = freebet.total_amount_given
        } else {
            this._model.winCounter.countWin(
                freebet.total_win + (freebet.triggered_win_amount_currency || 0) - (win_currency || 0),
                0
            )

            const initMultiplier: number = this._getInitMultiplier()
            if (initMultiplier > 0) {
                GameSignals.updateJarValue.emit(initMultiplier, false)
            }

            if (freebet.amount_left === 0) {
                GameSignals.balanceHasChanged.emit(
                    this._model.balance - (freebet.total_win + freebet.triggered_win_amount_currency)
                )
            }
        }
    }

    private _restoreRespinsSession(): void {
        this._nextState = GameStatesEnum.START_ROUND_STATE
        this._model.roundState.isRestoredRound = true
        this._model.spinState.isHoldAndWinActive = true
        this._model.isBonusGameNow = true
        this._model.winCounter.startRound()

        if (isHoldAndWinInsideFreeSpins(this._model)) {
            this._model.roundState.isFreeSpins = true
            this.seedNestedFreeSpinsLadderRuntime()
        }

        this._waitRestoreConfirmationOrContinue()
        GameSignals.enableGamePopups.emit()
        GameSignals.previousRoundActive.emit()
    }

    private seedNestedFreeSpinsLadderRuntime(): void {
        const extra = this._model.spinData?.hold_and_win_extra as any
        const milestones = Array.isArray(extra?.ladder_unlock_milestones_count)
            ? extra.ladder_unlock_milestones_count
                  .map((value: any) => Number(value))
                  .filter((value: number) => Number.isFinite(value))
            : []

        if (!milestones.length) {
            return
        }

        ;(Facade as any).fsRuntimeLadderMilestones = milestones.slice()
        const ladderCount = Number(extra?.ladder_count)
        ;(Facade as any).fsRuntimeLadderCount = Number.isFinite(ladderCount) ? ladderCount : -1
    }

    private _restoreSession(): void {
        if (RoundRestoreUtil.isRoundActive()) {
            this._nextState = GameStatesEnum.FINAL_STATE
            GameSignals.balanceHasChanged.emit(this._model.balance - this._model.spinData.win_currency)

            const initMultiplier: number = this._getInitMultiplier()
            if (initMultiplier > 0) {
                GameSignals.updateJarValue.emit(initMultiplier, false)
            }

            this._waitRestoreConfirmationOrContinue()
            GameSignals.previousRound.emit()
            //RoundRestoreUtil.stopRound()
            return
        }

        this.end()
    }

    private _getInitMultiplier = (): number => {
        let multiplier: number = 0

        this._model.spinData.slot_grid[0].forEach((reel: SymbolData[]) => {
            reel.forEach((symbolData: SymbolData) => {
                if (symbolData.id === SpecialSymbolType.MULTIPLIER) {
                    multiplier += symbolData.multiplier
                }
            })
        })

        return multiplier
    }

    public end = (button: string = null) => {
        const okButton = TranslationUtils.translate('okButton')

        if (button == null || button === `ui-dialog-btn_${okButton}`) {
            this._stateMachine.setState(this._nextState || GameStatesEnum.IDLE_STATE)
        }
    }

    public cleanUp() {
        if (this._autoContinueTimeoutId !== null) {
            clearTimeout(this._autoContinueTimeoutId)
            this._autoContinueTimeoutId = null
        }

        GameSignals.buttonClicked.disconnect(this.end)
    }

    private _waitRestoreConfirmationOrContinue(): void {
        GameSignals.buttonClicked.connect(this.end)
    }
}
